import { useState, useRef } from 'preact/hooks';
import type { Contact } from '../../types/contact';
import { activeContactSignal, selectContact } from '../../state/contacts.store';
import { formatRecentTime } from '../../utils/formatters';
import { Users, Trash2 } from 'lucide-preact';

interface ContactCardProps {
  contact: Contact;
  onContextMenu?: (e: MouseEvent, contact: Contact) => void;
  onLongPress?: (e: TouchEvent, contact: Contact) => void;
  onRequestDelete?: (contact: Contact) => void;
}

export function ContactCard({ contact, onContextMenu, onLongPress, onRequestDelete }: ContactCardProps) {
  const activeContact = activeContactSignal.value;
  const isActive = activeContact?.id === contact.id;
  const isGroup = Boolean(contact.is_group || (contact.jid && contact.jid.includes('@g.us')));
  const initial = contact.name ? contact.name.charAt(0).toUpperCase() : '?';

  const [swipeOffset, setSwipeOffset] = useState<number>(0);
  const [imgError, setImgError] = useState<boolean>(false);
  const touchStartXRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);
  const isSwipingRef = useRef<boolean>(false);
  const longPressTimerRef = useRef<any>(null);

  const showPic = Boolean(contact.profile_pic_url && !imgError);

  let lastMsgPreview = contact.phone_number;
  if (contact.last_message_content && contact.last_message_content.trim()) {
    const prefix = contact.last_message_sender === 'user' ? 'Você: ' : '';
    lastMsgPreview = `${prefix}${contact.last_message_content}`;
  }

  const timeFormatted = formatRecentTime(contact.last_message_at || contact.created_at);

  const isDraggingRef = useRef<boolean>(false);
  const [isDraggingState, setIsDraggingState] = useState<boolean>(false);
  const startXRef = useRef<number>(0);
  const startYRef = useRef<number>(0);

  const handleDragStart = (clientX: number, clientY: number, e?: Event) => {
    startXRef.current = clientX;
    startYRef.current = clientY;
    isDraggingRef.current = true;
    setIsDraggingState(true);
    isSwipingRef.current = false;

    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
    }
    longPressTimerRef.current = setTimeout(() => {
      if (!isSwipingRef.current && onLongPress && e) {
        onLongPress(e as any, contact);
      }
    }, 500);
  };

  const handleDragMove = (clientX: number, clientY: number) => {
    if (!isDraggingRef.current) return;
    const deltaX = clientX - startXRef.current;
    const deltaY = clientY - startYRef.current;

    // If movement is larger than 10px, cancel long press timer
    if (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10) {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }

    // Only swipe horizontally
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX < 0) {
        isSwipingRef.current = true;
        const baseOffset = swipeOffset === -80 ? -80 : 0;
        const offset = Math.max(-80, baseOffset + deltaX);
        setSwipeOffset(offset);
      } else if (deltaX > 0 && swipeOffset < 0) {
        isSwipingRef.current = true;
        const offset = Math.min(0, -80 + deltaX);
        setSwipeOffset(offset);
      }
    }
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;
    setIsDraggingState(false);
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    if (swipeOffset < -35) {
      setSwipeOffset(-80); // Snap open to reveal delete button
    } else {
      setSwipeOffset(0); // Snap shut
    }
  };

  const handleCardClick = () => {
    if (isSwipingRef.current || swipeOffset < -10) {
      if (swipeOffset < 0) {
        setSwipeOffset(0);
      }
      return;
    }
    selectContact(contact);
  };

  return (
    <div class="relative overflow-hidden rounded-xl my-0.5 group touch-pan-y">
      {/* Hidden Swipe Action Layer (Red Delete Button for Mobile & Desktop Swipe) */}
      <div
        class={`absolute inset-y-0 right-0 w-20 bg-rose-600 hover:bg-rose-500 flex items-center justify-center text-white z-0 rounded-r-xl transition-opacity duration-200 ${
          swipeOffset < 0 ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            setSwipeOffset(0);
            if (onRequestDelete) {
              onRequestDelete(contact);
            }
          }}
          class="w-full h-full flex flex-col items-center justify-center gap-1 text-white cursor-pointer hover:bg-rose-700/80 active:scale-95 transition-all"
          title="Excluir Conversa"
        >
          <Trash2 class="w-4 h-4 text-white" />
          <span class="text-[10px] font-bold">Excluir</span>
        </button>
      </div>

      {/* Main Card Element */}
      <div
        onClick={handleCardClick}
        onContextMenu={(e) => {
          if (onContextMenu) {
            e.preventDefault();
            onContextMenu(e, contact);
          }
        }}
        onTouchStart={(e) => handleDragStart(e.touches[0].clientX, e.touches[0].clientY, e)}
        onTouchMove={(e) => handleDragMove(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchEnd={handleDragEnd}
        onTouchCancel={handleDragEnd}
        onPointerDown={(e) => {
          if (e.pointerType === 'touch') return;
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          handleDragStart(e.clientX, e.clientY, e);
        }}
        onPointerMove={(e) => {
          if (e.pointerType === 'touch') return;
          handleDragMove(e.clientX, e.clientY);
        }}
        onPointerUp={(e) => {
          if (e.pointerType === 'touch') return;
          handleDragEnd();
        }}
        onPointerCancel={(e) => {
          if (e.pointerType === 'touch') return;
          handleDragEnd();
        }}
        style={{ transform: `translateX(${swipeOffset}px)` }}
        class={`contact-card relative z-10 ${isDraggingState ? 'transition-none' : 'transition-transform duration-200 ease-out'} select-none ${isActive ? 'active' : ''}`}
      >
        <div class="flex items-center gap-3 min-w-0 flex-1">
          <div class={`contact-avatar ${isGroup ? 'group' : ''} ${isActive ? 'active' : ''} overflow-hidden`}>
            {showPic ? (
              <img
                src={contact.profile_pic_url!}
                alt={contact.name}
                referrerPolicy="no-referrer"
                class="w-full h-full object-cover rounded-full"
                onError={() => setImgError(true)}
              />
            ) : isGroup ? (
              <Users class="w-4 h-4 text-purple-300" />
            ) : (
              initial
            )}
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center justify-between gap-1 mb-0.5">
              <div class="flex items-center gap-1.5 min-w-0">
                <h4 class={`text-xs font-bold truncate ${isActive ? 'text-cyan-300' : 'text-slate-100'}`}>
                  {contact.name}
                </h4>
                {isGroup && (
                  <span class="px-1.5 py-0.2 rounded text-[9px] font-bold bg-purple-500/20 text-purple-300 border border-purple-400/30 flex-shrink-0">
                    Grupo
                  </span>
                )}
              </div>
              {timeFormatted && (
                <span class="text-[10px] font-mono text-slate-400 flex-shrink-0 ml-1">
                  {timeFormatted}
                </span>
              )}
            </div>
            <p class="text-[11px] text-slate-400 truncate font-normal">{lastMsgPreview}</p>
          </div>
        </div>

        {contact.pending_actions_count > 0 && (
          <div class="flex items-center gap-1.5 ml-2 flex-shrink-0">
            <span class="badge-pending" title={`${contact.pending_actions_count} pendência(s)`}>
              {contact.pending_actions_count}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
